// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {ConnectionQuality, Participant, Room} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceParticipantManager');
export type LivekitParticipantSnapshot = Readonly<{
	identity: string;
	userId: string | null;
	connectionId: string | null;
	sid: string;
	isLocal: boolean;
	isSpeaking: boolean;
	isAudioLevelSpeaking: boolean;
	connectionQuality: ConnectionQuality;
	metadata?: string;
	attributes: Readonly<Record<string, string>>;
	audioTrackSids: ReadonlyArray<string>;
	videoTrackSids: ReadonlyArray<string>;
	isMicrophoneEnabled: boolean;
	isCameraEnabled: boolean;
	isScreenShareEnabled: boolean;
	joinedAt: number | null;
	lastSpokeAt: number | null;
}>;
export function isParticipantSnapshotSpeaking(snapshot: LivekitParticipantSnapshot | undefined): boolean {
	return Boolean(snapshot && (snapshot.isSpeaking || snapshot.isAudioLevelSpeaking));
}
const extractUserId = (identity: string): string | null => {
	const match = identity.match(/^user_(\d+)(?:_(.+))?$/);
	return match ? match[1] : null;
};
const extractConnectionId = (identity: string): string | null => {
	const match = identity.match(/^user_(\d+)_(.+)$/);
	return match ? match[2] : null;
};
const keysSorted = (m: Map<string, unknown>): ReadonlyArray<string> => Object.freeze([...m.keys()].sort());
const attrsClone = (a: Readonly<Record<string, string>>): Readonly<Record<string, string>> => Object.freeze({...a});
const arraysEqual = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean => {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
};
const createSnapshot = (p: Participant, previous?: LivekitParticipantSnapshot): LivekitParticipantSnapshot => ({
	identity: p.identity,
	userId: extractUserId(p.identity),
	connectionId: extractConnectionId(p.identity),
	sid: p.sid,
	isLocal: p.isLocal,
	isSpeaking: p.isSpeaking,
	isAudioLevelSpeaking: previous?.isAudioLevelSpeaking ?? false,
	connectionQuality: p.connectionQuality,
	metadata: p.metadata ?? undefined,
	attributes: attrsClone(p.attributes),
	audioTrackSids: keysSorted(p.audioTrackPublications),
	videoTrackSids: keysSorted(p.videoTrackPublications),
	isMicrophoneEnabled: p.isMicrophoneEnabled,
	isCameraEnabled: p.isCameraEnabled,
	isScreenShareEnabled: p.isScreenShareEnabled,
	joinedAt: p.joinedAt ? p.joinedAt.getTime() : null,
	lastSpokeAt: p.lastSpokeAt ? p.lastSpokeAt.getTime() : null,
});
const snapshotsEqual = (a: LivekitParticipantSnapshot | undefined, b: LivekitParticipantSnapshot): boolean => {
	if (!a) return false;
	return (
		a.identity === b.identity &&
		a.sid === b.sid &&
		a.isLocal === b.isLocal &&
		a.isSpeaking === b.isSpeaking &&
		a.isAudioLevelSpeaking === b.isAudioLevelSpeaking &&
		a.connectionQuality === b.connectionQuality &&
		a.metadata === b.metadata &&
		a.isMicrophoneEnabled === b.isMicrophoneEnabled &&
		a.isCameraEnabled === b.isCameraEnabled &&
		a.isScreenShareEnabled === b.isScreenShareEnabled &&
		a.joinedAt === b.joinedAt &&
		a.lastSpokeAt === b.lastSpokeAt &&
		arraysEqual(a.audioTrackSids, b.audioTrackSids) &&
		arraysEqual(a.videoTrackSids, b.videoTrackSids) &&
		attrsEqual(a.attributes, b.attributes)
	);
};
const attrsEqual = (a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): boolean => {
	if (a === b) return true;
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	if (keysA.length !== keysB.length) return false;
	for (let i = 0; i < keysA.length; i++) {
		const k = keysA[i];
		if (a[k] !== b[k]) return false;
	}
	return true;
};
class VoiceParticipantManager {
	private _participants: Readonly<Record<string, LivekitParticipantSnapshot>> = {};
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	get participants(): Readonly<Record<string, LivekitParticipantSnapshot>> {
		return this._participants;
	}
	upsertParticipant(participant: Participant): void {
		const existing = this._participants[participant.identity];
		const newSnap = createSnapshot(participant, existing);
		if (snapshotsEqual(existing, newSnap)) {
			return;
		}
		runInAction(() => {
			this._participants = {
				...this._participants,
				[participant.identity]: newSnap,
			};
		});
		logger.debug('Updated', {identity: participant.identity, isLocal: participant.isLocal});
	}
	setAudioLevelSpeaking(identity: string, speaking: boolean): void {
		const existing = this._participants[identity];
		if (!existing) return;
		if (existing.isAudioLevelSpeaking === speaking) return;
		runInAction(() => {
			this._participants = {
				...this._participants,
				[identity]: {...existing, isAudioLevelSpeaking: speaking},
			};
		});
	}
	removeParticipant(identity: string): void {
		if (!(identity in this._participants)) {
			return;
		}
		runInAction(() => {
			const next = {...this._participants};
			delete next[identity];
			this._participants = next;
		});
		logger.debug('Removed', {identity});
	}
	hydrateFromRoom(room: Room): void {
		const next: Record<string, LivekitParticipantSnapshot> = {};
		const previous = this._participants;
		if (room.localParticipant) {
			next[room.localParticipant.identity] = createSnapshot(
				room.localParticipant,
				previous[room.localParticipant.identity],
			);
		}
		room.remoteParticipants.forEach((participant) => {
			next[participant.identity] = createSnapshot(participant, previous[participant.identity]);
		});
		runInAction(() => {
			this._participants = next;
		});
		logger.info('Hydrated participants', {count: Object.keys(next).length});
	}
	updateActiveSpeakers(speakers: Array<Participant>): void {
		const speakerIds = new Set(speakers.map((s) => s.identity));
		let changed = false;
		const next = {...this._participants};
		for (const [identity, snap] of Object.entries(this._participants)) {
			const shouldBeSpeaking = speakerIds.has(identity);
			if (snap.isSpeaking !== shouldBeSpeaking) {
				next[identity] = {...snap, isSpeaking: shouldBeSpeaking};
				changed = true;
			}
		}
		if (changed) {
			runInAction(() => {
				this._participants = next;
			});
		}
	}
	getParticipantByUserIdAndConnectionId(
		userId: string,
		connectionId: string | null,
	): LivekitParticipantSnapshot | undefined {
		for (const participant of Object.values(this._participants)) {
			if (participant.userId === userId && participant.connectionId === connectionId) {
				return participant;
			}
		}
		return undefined;
	}
	getParticipant(identity: string): LivekitParticipantSnapshot | undefined {
		return this._participants[identity];
	}
	getLocalParticipant(): LivekitParticipantSnapshot | undefined {
		for (const participant of Object.values(this._participants)) {
			if (participant.isLocal) {
				return participant;
			}
		}
		return undefined;
	}
	clear(): void {
		runInAction(() => {
			this._participants = {};
		});
		logger.debug('All participants cleared');
	}
	extractUserId(identity: string): string | null {
		return extractUserId(identity);
	}
	extractConnectionId(identity: string): string | null {
		return extractConnectionId(identity);
	}
}
const instance = new VoiceParticipantManager();
(
	window as typeof window & {
		_voiceParticipantManager?: VoiceParticipantManager;
	}
)._voiceParticipantManager = instance;
export default instance;
